import React, { useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Box,
} from "@mui/material";
import { formatDate } from "date-fns";
import PaginationControl from "components/shared/modal/PaginationControl";

interface BookingListTableProps {
  bookings: any[];
  visibleColumns: readonly any[];
  isMobile: boolean;
  onRowClick: (row: any) => void;
  getStatusColor: (status: string) => string;
  page: number;
  onPageChange: (event: React.ChangeEvent<unknown> | null, page: number) => void;
  itemsPerPage?: number;
  searchTerm?: string;
}

const BookingListTable = ({
  bookings,
  visibleColumns,
  isMobile,
  onRowClick,
  getStatusColor,
  page,
  onPageChange,
  itemsPerPage = 10,
  searchTerm = "",
}: BookingListTableProps) => {
  
  const tableData = useMemo(() => {
    const startIndex = (page - 1) * itemsPerPage;
    return bookings.slice(startIndex, startIndex + itemsPerPage);
  }, [bookings, page, itemsPerPage]);

  if (bookings.length === 0) {
    return (
      <TableContainer>
        <Table>
          <TableBody>
            <TableRow>
              <TableCell
                colSpan={visibleColumns.length}
                sx={{
                  textAlign: "center",
                  padding: "40px 0",
                  color: "#6B7280",
                }}
              >
                 <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 2,
                  }}
                >
                  <Typography variant="h6">
                    {searchTerm ? "No results found" : "No bookings in this group"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {searchTerm
                      ? "Try using different search terms"
                      : "Try changing the status filter or search term"}
                  </Typography>
                </Box>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  return (
    <>
      <TableContainer sx={{ maxHeight: "calc(100vh - 300px)" }}>
        <Table
          stickyHeader
          sx={{ minWidth: isMobile ? 350 : 700 }}
          size={isMobile ? "small" : "medium"}
        >
          <TableHead>
            <TableRow>
              {visibleColumns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align || "left"}
                  style={{
                    minWidth: column.minWidth,
                    padding: isMobile ? "10px" : "14px",
                  }}
                  sx={{
                    color: "#717680",
                    fontWeight: "600",
                    fontSize: isMobile ? "13px" : "14px",
                    bgcolor: "#FAFAFA",
                    fontFamily: "Inter",
                    position: "sticky",
                    zIndex: 100,
                    boxShadow:
                      column.id === "title"
                        ? "2px 0px 3px rgba(0,0,0,0.05)"
                        : "none",
                    ...column.sx,
                  }}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {tableData.map((row: any, index: number) => (
              <TableRow
                key={row.id || `row-${index}`}
                sx={{
                  cursor: "pointer",
                  "&:hover": { backgroundColor: "#f5f5f5" },
                  "&:nth-of-type(even)": {
                    backgroundColor: "rgba(0, 0, 0, 0.02)",
                  },
                  transition: "background-color 0.2s ease",
                }}
              >
                {visibleColumns.map((column) => {
                  if (column.id === "tripId") {
                    return (
                      <TableCell
                        key={column.id}
                        sx={{
                          color: "#535862",
                          fontWeight: 500,
                          textAlign: "center",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: isMobile ? "120px" : "200px",
                          padding: isMobile ? "10px" : "16px",
                        }}
                        onClick={() => onRowClick(row)}
                      >
                        {row?.tripIdDisplay ?? "N/A"}
                      </TableCell>
                    );
                  } else if (column.id === "title") {
                    return (
                      <TableCell
                        key={column.id}
                        sx={{
                          color: "#535862",
                          fontWeight: 400,
                          textAlign: "center",
                          padding: isMobile ? "10px" : "16px",
                        }}
                        onClick={() => onRowClick(row)}
                      >
                        <Typography
                          component="div"
                          sx={{
                            maxWidth: "100%",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            lineHeight: "1.4em",
                            maxHeight: "2.8em",
                          }}
                        >
                          {row?.tripTitle ?? "N/A"}
                        </Typography>
                      </TableCell>
                    );
                  } else {
                    return (
                      <TableCell
                        key={column.id}
                        sx={{
                          color: "#535862",
                          fontWeight: 400,
                          textAlign: "center",
                          padding: isMobile ? "10px" : "16px",
                        }}
                        onClick={() => onRowClick(row)}
                      >
                        {column.id === "startDate" ? (
                          formatDate(row?.slotStartDate, "dd MMM yyyy hh:mm a")
                        ) : column.id === "bookingStatus" ? (
                          <Chip
                            label={`${row?.bookingStatus}`}
                            color={getStatusColor(row?.bookingStatus) as any}
                            variant="filled"
                            size="small"
                            sx={{
                              marginLeft: { xs: 0, md: "auto" },
                              fontWeight: 500,
                              height: 24,
                            }}
                          />
                        ) : column.id === "bookingAmount" ? (
                          row?.transactionAmountRazorpay / 100
                        ) : column.id === "numberofTravellers" ? (
                          row?.numberOfTravellers ?? 0
                        ) : column.id === "createdAt" ? (
                          formatDate(row?.createdAt, "dd MMM yyyy hh:mm a")
                        ) : (
                          "N/A"
                        )}
                      </TableCell>
                    );
                  }
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {bookings.length > 0 && (
        <PaginationControl
          totalRows={bookings.length}
          page={page}
          onPageChange={onPageChange}
        />
      )}
    </>
  );
};

export default React.memo(BookingListTable);